import React, { useEffect, useState } from "react";
import {
  CardContent,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import LoadingSpinner from "../../../LoadingSpinner";
import {
  useGetAllAssigneeBySelectedTeamIdQuery,
  useGetAllWorkflowsQuery,
  useGetAllTeamsSelectedWorkflowIdQuery,
  useGetTicketLifeCycleReportQuery,
  useLazyGetTicketLifeCycleReportDownloadQuery,
} from "../../../../Services/reports/tickets/ReportTickets";
import { toast } from "react-toastify";
import CsvDownloader from "react-csv-downloader";
import { AUTH_TOKEN } from "../../../../constants";

const getTodayDateString = () => new Date().toISOString().split("T")[0];
const isCurrentDateRange = (from: string, to: string) => from === getTodayDateString() && to === getTodayDateString();
type ReportMode = "live" | "historical" | null;

const normalizeLifeCycleResults = (data: unknown): any[] => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj.results)) return obj.results;
    if (Array.isArray(obj.data)) return obj.data;
  }
  return [];
};

const areReportParamsEqual = (a: any, b: any) => {
  if (!a || !b) return false;
  return JSON.stringify(a) === JSON.stringify(b);
};

const TicketLifeCycleReport = () => {
  const navigate = useNavigate();
  const [columns, setColumns] = useState<GridColDef[]>([
    {
      field: "createdDate",
      headerName: "Created Date",
      flex: 1,
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "title",
      headerName: "Ticket Id",
      flex: 1,
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "created",
      headerName: "Last Updated",
      flex: 1,
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "status_type",
      headerName: "Status",
      flex: 1,
      minWidth: 120,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "description",
      headerName: "Agent",
      flex: 1,
      minWidth: 120,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "created_by.username",
      headerName: "Creator",
      flex: 1,
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },

    {
      field: "priority",
      headerName: "Ticket Name",
      flex: 1,
      minWidth: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "totalTransactionTime",
      headerName: "Total Transaction Time (HH:MM:SS)",
      flex: 1,
      minWidth: 300,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "is_non_actionable",
      headerName: "Is Non-Actionable",
      flex: 1,
      minWidth: 150,
      align: "center",
      headerAlign: "center",
      valueGetter: (value: any) => (value || "-"),
    },
    {
      field: "non_actionable_marked_by",
      headerName: "Non-Actionable marked by",
      flex: 1,
      minWidth: 150,
      align: "center",
      headerAlign: "center",
      valueGetter: (value: any) => value || "-",
    },
    {
      field: "non_actionable_marked_at",
      headerName: "Non-Actionable marked at",
      flex: 1,
      minWidth: 180,
      align: "center",
      headerAlign: "center",
      valueGetter: (value: any) => value || "-",
    },
    {
      field: "non_actionable_marked_method",
      headerName: "Non-Actionable type",
      flex: 1,
      minWidth: 180,
      align: "center",
      headerAlign: "center",
      valueGetter: (value: any) => {
        if (!value) return "-";
        if (value === "manual") return "Manual";
        if (value === "automatic") return "Automatic";
        return value;
      },
    },
  ]);
  const { data, isLoading } = useGetAllWorkflowsQuery({});
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(
    null,
  );
  const [selectedTeams, setSelectedTeams] = useState<any>([]);
  const [selectedTeamsParam, setSelectedTeamsParam] = useState<string[]>([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 20,
    page: 0,
  });
  const [localLoading, setLocalLoading] = useState(false);
  const { data: assigneeData, isLoading: assigneeLoading } =
    useGetAllAssigneeBySelectedTeamIdQuery(selectedTeams[0], {
      skip: !selectedTeams || selectedTeams.length === 0,
    });
  const { data: teamData, isLoading: teamLoading } =
    useGetAllTeamsSelectedWorkflowIdQuery(selectedWorkflowId, {
      skip: !selectedWorkflowId,
    });

  const [optionsTeams, setOptionsTeams] = useState<any[]>([]);
  const [optionsAssignee, setOptionsAssignee] = useState<any[]>([]);
  const [optionsWorkflow, setOptionsWorkflow] = useState<any[]>([]);
  const [selectedAssignee, setSelectedAssignee] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [params, setParams] = useState<any>(null);

  const [historicalMeta, setHistoricalMeta] = useState<{
    count: number;
    current_page: number;
    page_size: number;
  } | null>(null);
  const [historicalRawResults, setHistoricalRawResults] = useState<any[]>([]);
  const [dataRows, setDataRows] = useState<any[]>([]);
  const [reportMode, setReportMode] = useState<ReportMode>(null);
  const [reportFetching, setReportFetching] = useState(false);

  const {
    data: liveLifeCycleReportData,
    isFetching: liveReportFetching,
    status: liveReportStatus,
    refetch: refetchLiveReport,
  } = useGetTicketLifeCycleReportQuery(params, {
    skip: !params || reportMode !== "live",
  });

  const [triggerTicketLifeCycleDownload, { isFetching: downloadLoading }] =
    useLazyGetTicketLifeCycleReportDownloadQuery();

  const reportingBaseUrl =
    process.env.REACT_APP_REPORTING_SERVER_URL || "https://reporting.bluewhirl.org";

  useEffect(() => {
    if (!params || reportMode !== "historical") {
      if (reportMode !== "historical") {
        setHistoricalMeta(null);
        setHistoricalRawResults([]);
      }
      return;
    }

    const controller = new AbortController();
    setLocalLoading(true);
    setReportFetching(true);

    const queryParams: Record<string, string | number | boolean> = {
      from_date: params.gte,
      to_date: params.lte,
      page: params.page ?? 1,
      page_size: params.page_size ?? 20,
      report_type: "audit",
      download: false,
    };
    if (params.workflow_id) queryParams.workflow_id = params.workflow_id;
    if (params.team_ids?.length) {
      queryParams.team_ids = Array.isArray(params.team_ids) ? params.team_ids.join(",") : String(params.team_ids);
    } else if (params.team_id != null && params.team_id !== "") {
      queryParams.team_ids = String(params.team_id);
    }
    if (params.assignee) queryParams.assignee = params.assignee;

    axios
      .get(`${reportingBaseUrl}/ticket/lifecycle`, {
        params: queryParams,
        signal: controller.signal,
        headers: {
          Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN)}`,
        },
      })
      .then((res) => {
        const data = res?.data;
        const results = normalizeLifeCycleResults(data);
        const count =
          data?.total_rows ?? data?.count ?? data?.total ?? results.length;
        const serverPage = data?.current_page ?? data?.page ?? params.page ?? 1;
        const serverPageSize =
          data?.page_size ?? params.page_size ?? paginationModel.pageSize;
        const apiPage = Number(serverPage);
        const pageSizeNum = Number(serverPageSize);

        setHistoricalMeta({
          count: typeof count === "number" ? count : results.length,
          current_page: apiPage,
          page_size: pageSizeNum,
        });
        setHistoricalRawResults(results);
        setPaginationModel({
          page: Math.max(0, apiPage - 1),
          pageSize: pageSizeNum,
        });
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        toast.error(err?.response?.data?.message || "Failed to fetch ticket lifecycle report");
        setHistoricalMeta(null);
        setHistoricalRawResults([]);
        setDataRows([]);
      })
      .finally(() => {
        setLocalLoading(false);
        setReportFetching(false);
      });

    return () => controller.abort();
  }, [params, reportingBaseUrl, reportMode]);

  useEffect(() => {
    if (reportMode === "live") {
      if (liveLifeCycleReportData === undefined || liveReportFetching) return;
      const results = normalizeLifeCycleResults(liveLifeCycleReportData);
      setDataRows(formatRows(results, columns));
      setLocalLoading(false);
      return;
    }
    if (reportMode === "historical") {
      const currentPage = historicalMeta?.current_page ?? 1;
      const pageSize = historicalMeta?.page_size ?? paginationModel.pageSize;
      setDataRows(formatRows(historicalRawResults, columns, currentPage, pageSize));
    }
  }, [
    reportMode,
    liveLifeCycleReportData,
    liveReportFetching,
    historicalRawResults,
    historicalMeta,
    columns,
    paginationModel.pageSize,
  ]);

  useEffect(() => {
    if (teamData && teamData.results?.length > 0) {
      const transformedOptions = teamData.results.map((team: any) => ({
        label: team.team_name,
        value: team.id,
      }));
      setOptionsTeams(transformedOptions);
    }
  }, [teamData]);

  useEffect(() => {
    if (assigneeData) {
      const transformedAssignees = assigneeData.map((assignee: any) => ({
        label: `${assignee.username}`,
        value: assignee.id,
      }));
      setOptionsAssignee(transformedAssignees);
    }
  }, [assigneeData]);

  useEffect(() => {
    if (data && data.results?.length > 0) {
      const transformedWorkflows = data.results.map((workflow: any) => ({
        label: workflow.workflow_name,
        value: workflow.id,
      }));
      setOptionsWorkflow(transformedWorkflows);
    }
  }, [data]);

  const handleTeamsChange = (event: any) => {
    const {
      target: { value },
    } = event;

    if (value.includes("all")) {
      setSelectedTeams(
        selectedTeams.length === optionsTeams.length
          ? []
          : optionsTeams.map((team) => team.value),
      );
    } else {
      // Convert the value to an array of strings
      setSelectedTeams(typeof value === "string" ? value.split(",") : value);
    }
  };

  useEffect(() => {
    setSelectedTeamsParam(selectedTeams);
  }, [selectedTeams]);

  const isAllSelected = selectedTeams.length === optionsTeams.length;

  const handleAssigneeChange = (event: any) => {
    setSelectedAssignee(event.target.value);
  };

  const handleWorkflowChange = (event: any) => {
    setSelectedWorkflowId(event.target.value);
  };

  const handleDateFromChange = (event: any) => {
    setDateFrom(event.target.value);
  };

  const handleDateToChange = (event: any) => {
    setDateTo(event.target.value);
  };

  const handleSubmit = () => {
    if (!dateFrom) {
      toast.error('Please select "From Date"');
      return;
    }
    if (!dateTo) {
      toast.error('Please select "To Date"');
      return;
    }
    if (!selectedTeamsParam.length) {
      toast.error('Please select "Team"');
      return;
    }

    const isLive = isCurrentDateRange(dateFrom, dateTo);
    if (!isLive && !selectedAssignee) {
      toast.error('Please select "Assignee"');
      return;
    }

    const queryParams: any = {
      gte: dateFrom,
      lte: dateTo,
    };

    if (selectedWorkflowId) {
      queryParams.workflow_id = selectedWorkflowId;
    }

    if (selectedTeamsParam.length > 1) {
      queryParams.team_ids = selectedTeamsParam;
    } else if (selectedTeamsParam.length === 1) {
      queryParams.team_id = selectedTeamsParam[0];
    }

    if (isLive) {
      if (selectedAssignee && selectedAssignee !== "All") {
        queryParams.action__assignee = selectedAssignee;
      }
    } else {
      queryParams.assignee = selectedAssignee;
      queryParams.page = 1;
      queryParams.page_size = paginationModel.pageSize;
    }

    setReportMode(isLive ? "live" : "historical");
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    setHistoricalMeta(null);
    setHistoricalRawResults([]);

    if (isLive && params && areReportParamsEqual(params, queryParams)) {
      setLocalLoading(true);
      setDataRows([]);
      void refetchLiveReport();
      return;
    }

    setDataRows([]);
    setParams(queryParams);
  };

  const resetFilters = () => {
    setSelectedTeams([]);
    setSelectedAssignee(null);
    setSelectedWorkflowId(null);
    setDateFrom("");
    setDateTo("");
    setParams(null);
    setReportMode(null);
    setHistoricalMeta(null);
    setHistoricalRawResults([]);
    setDataRows([]);
    setPaginationModel({ page: 0, pageSize: 20 });
  };

  const formatRows = (
    data1: unknown,
    columns: GridColDef[],
    currentPage?: number,
    pageSize?: number,
  ) => {
    const results = normalizeLifeCycleResults(data1);
    return results.map((item, index) => {
      const row: any = {
        id:
          currentPage != null && pageSize != null
            ? (currentPage - 1) * pageSize + index
            : index + 1,
      };

      columns.forEach((column) => {
        if (column.field === "createdDate") {
          row[column.field] = item.created || "-";
        } else if (column.field === "title") {
          row[column.field] = item.ticket_id || "-";
        } else if (column.field === "status_type") {
          row[column.field] = item.status || "-";
        } else if (column.field === "created") {
          row[column.field] = item.last_update || "-";
        } else if (column.field === "description") {
          row[column.field] = item.agent?.username || "-";
        } else if (column.field === "created_by.username") {
          row[column.field] = item.created_by?.username || "-";
        } else if (column.field === "totalTransactionTime") {
          row[column.field] = item.total_transaction_time || "-";
        } else if (column.field === "priority") {
          row[column.field] = item.workflow_name || "-";
        } else if (column.field === "is_non_actionable") {
          row[column.field] = item.is_non_actionable || "-";
        } else if (column.field === "non_actionable_marked_by") {
          row[column.field] = item.non_actionable_marked_by || "-";
        } else if (column.field === "non_actionable_marked_at") {
          row[column.field] = item.non_actionable_marked_at || "-";
        } else if (column.field === "non_actionable_marked_method") {
          row[column.field] = item.non_actionable_marked_method || "-";
        } else {
          // Handle transaction_time fields
          const transaction = item.transaction_time?.find(
            (t: any) => t.status === column.field,
          );
          row[column.field] = transaction?.transaction_time || "-";
        }
      });

      return row;
    });
  };

  // non_actionable_marked_by: item.non_actionable_marked_by || "-",
  //     non_actionable_marked_at: item.non_actionable_marked_at || "-",
  //     non_actionable_marked_method: item.non_actionable_marked_method || "-",
  useEffect(() => {
    setColumns([
      {
        field: "createdDate",
        headerName: "Created date",
        flex: 1,
        minWidth: 150,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "title",
        headerName: "Ticket id",
        flex: 1,
        minWidth: 150,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "created",
        headerName: "Last updated",
        flex: 1,
        minWidth: 150,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "status_type",
        headerName: "Status",
        flex: 1,
        minWidth: 120,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "description",
        headerName: "Agent",
        flex: 1,
        minWidth: 120,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "created_by.username",
        headerName: "Creator",
        flex: 1,
        minWidth: 150,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "priority",
        headerName: "Ticket name",
        flex: 1,
        minWidth: 150,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "totalTransactionTime",
        headerName: "Total transaction time (HH:MM:SS)",
        flex: 1,
        minWidth: 300,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "is_non_actionable",
        headerName: "Is Non-Actionable",
        flex: 1,
        minWidth: 150,
        align: "center",
        headerAlign: "center",
        valueGetter: (value: any) => (value || "-"),
      },
      {
        field: "non_actionable_marked_by",
        headerName: "Non-Actionable marked by",
        flex: 1,
        minWidth: 150,
        align: "center",
        headerAlign: "center",
        valueGetter: (value: any) => value || "-",
      },
      {
        field: "non_actionable_marked_at",
        headerName: "Non-Actionable marked at",
        flex: 1,
        minWidth: 180,
        align: "center",
        headerAlign: "center",
        valueGetter: (value: any) => value || "-",
      },
      {
        field: "non_actionable_marked_method",
        headerName: "Non-Actionable type",
        flex: 1,
        minWidth: 180,
        align: "center",
        headerAlign: "center",
        valueGetter: (value: any) => {
          if (!value) return "-";
          if (value === "manual") return "Manual";
          if (value === "automatic") return "Automatic";
          return value;
        },
      },
    ]);

    const reportResults =
      reportMode === "live"
        ? normalizeLifeCycleResults(liveLifeCycleReportData)
        : historicalRawResults;
    if (Array.isArray(reportResults) && reportResults.length > 0) {
      const statusColumns2 = reportResults[0]?.transaction_time
        ?.map((item) => item.status)
        .filter(
          (status, index, self) => status && self.indexOf(status) === index,
        )
        .map((status) => ({
          field: status === null ? "null" : status,
          headerName: `Transaction Time (HH:MM:SS) ${status}`,
          flex: 1,
          minWidth: 300,
          align: "center",
          headerAlign: "center",
        }));

      if (Array.isArray(statusColumns2)) {
        setColumns((prevColumns) => [...prevColumns, ...statusColumns2]);
      }
    }
  }, [reportMode, liveLifeCycleReportData, historicalRawResults]);

  const rows = dataRows;

  const gridLoading =
    reportMode === "live" ? localLoading || liveReportFetching : localLoading;
  const showSpinner =
    isLoading ||
    assigneeLoading ||
    teamLoading ||
    (reportMode === "live"
      ? liveReportStatus === "pending"
      : reportFetching || localLoading);

  const handlePaginationChange = (newPaginationModel: any) => {
    setPaginationModel(newPaginationModel);
    if (!params) return;
    if (reportMode === "live") return;
    setLocalLoading(true);
    const updatedParams = {
      ...params,
      page: newPaginationModel.page + 1,
      page_size: newPaginationModel.pageSize,
    };
    setParams(updatedParams);
  };

  const handleDownload = async () => {
    if (!params) {
      toast.error("Please apply filters and submit before generating the report.");
      return;
    }

    const downloadPayload: Record<string, string | number> = {
      from_date: params.gte,
      to_date: params.lte,
      page: 1,
      page_size: historicalMeta?.count ?? 99999,
    };
    if (params.workflow_id) downloadPayload.workflow_id = params.workflow_id;
    if (params.team_ids?.length) {
      downloadPayload.team_ids = Array.isArray(params.team_ids)
        ? params.team_ids.join(",")
        : String(params.team_ids);
    } else if (params.team_id != null && params.team_id !== "") {
      downloadPayload.team_ids = String(params.team_id);
    }
    if (params.assignee) downloadPayload.assignee = params.assignee;

    try {
      await triggerTicketLifeCycleDownload(downloadPayload).unwrap();
      toast.success("Report generation in progress. Download from exported report section once ready");
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to queue report generation.");
    }
  };

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 120,
      },
    },
  };
  return (
    <div id="kt_app_content_container" className="app-container mx-5">
      <div className="card card-flush">
        {/* Card Header */}
        <div
          className="card-header align-items-center py-1 gap-2 gap-md-5"
          style={{ justifyContent: "flex-start" }}
        >
          {/* <img
            src="https://insurance.circleonecrm.com/assets/arrow_back.svg"
            alt="Back"
            style={{ marginBottom: "-2px", cursor: "pointer" }}
            className="ng-star-inserted"
            onClick={() => {
              navigate("/reports/ticket/reports-list");
            }}
          /> */}
          <button
            className="btn btn-link mb-2"
            onClick={() => {
              navigate("/reports/ticket/reports-list");
            }}
            style={{ marginRight: "-17px" }}
          >
            <i
              className="bi bi-arrow-90deg-left"
              style={{
                fontSize: "20px",
                color: "black",
                cursor: "pointer",
              }}
            ></i>
          </button>
          <span className="fs-3 text-gray-800 fw-bold ms-3">
            Ticket Lifecycle
          </span>
        </div>

        {/* Toolbar */}
        <div
          className="card-toolbar flex-row-fluid justify-content-start gap-5 d-flex mb-10"
          style={{ marginLeft: "30px" }}
        >
          {/* Date Filters */}
          <div className="w-100 mw-150px">
            <TextField
              id="date-from"
              label="From"
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                max: new Date().toISOString().split("T")[0],
                style: { textTransform: "uppercase" },
              }}
              fullWidth
              value={dateFrom}
              onChange={handleDateFromChange}
            />
          </div>
          <div className="w-100 mw-150px">
            <TextField
              id="date-to"
              label="To"
              type="date"
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                max: new Date().toISOString().split("T")[0],
                style: { textTransform: "uppercase" },
              }}
              fullWidth
              value={dateTo}
              onChange={handleDateToChange}
            />
          </div>

          <div className="w-100 mw-150px">
            <FormControl fullWidth>
              <InputLabel id="workflow-select-label">
                Select Workflow
              </InputLabel>
              <Select
                labelId="workflow-select-label"
                id="workflow-select"
                value={selectedWorkflowId}
                onChange={handleWorkflowChange}
                label="Workflow"
                MenuProps={MenuProps}
              >
                {optionsWorkflow.map((workflow) => (
                  <MenuItem key={workflow.value} value={workflow.value}>
                    {workflow.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className="w-100 mw-150px">
            <FormControl fullWidth>
              <InputLabel id="demo-multiple-checkbox-label">
                Select Teams
              </InputLabel>
              <Select
                labelId="demo-multiple-checkbox-label"
                id="demo-multiple-checkbox"
                multiple
                value={selectedTeams}
                onChange={handleTeamsChange}
                input={<OutlinedInput label="Select Teams" />}
                renderValue={(selected) => {
                  if (isAllSelected) {
                    return <div>All</div>;
                  }
                  return (
                    <div>
                      {selected
                        .map((teamId) => {
                          const team = optionsTeams.find(
                            (t) => t.value === teamId,
                          );
                          return team ? team.label : null;
                        })
                        .join(", ")}
                    </div>
                  );
                }}
                MenuProps={MenuProps}
              >
                <MenuItem
                  value="all"
                  onClick={() =>
                    setSelectedTeams(
                      isAllSelected
                        ? []
                        : optionsTeams.map((team) => team.value),
                    )
                  }
                >
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={selectedTeams?.length > 0 && !isAllSelected}
                  />
                  <ListItemText primary="All" />
                </MenuItem>
                {optionsTeams.map((team) => (
                  <MenuItem key={team.value} value={team.value}>
                    <Checkbox
                      checked={selectedTeams.indexOf(team.value) > -1}
                    />
                    <ListItemText primary={team.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div className="w-100 mw-150px">
            <FormControl fullWidth>
              <InputLabel id="Assignee-select-label">Assignee</InputLabel>
              <Select
                labelId="Assignee-select-label"
                id="Assignee-select"
                value={selectedAssignee ?? ""}
                onChange={handleAssigneeChange}
                label="Assignee"
                MenuProps={MenuProps}
              >
                <MenuItem value="All">All</MenuItem>
                {optionsAssignee.map(
                  (assignee) =>
                    assignee.label.length > 1 && (
                      <MenuItem key={assignee.value} value={assignee.value}>
                        {assignee.label}
                      </MenuItem>
                    ),
                )}
              </Select>
            </FormControl>
          </div>

          <div className="ms-auto d-flex gap-3" style={{ marginRight: "20px" }}>
            <button className="btn btn-primary" onClick={handleSubmit}>
              Submit
            </button>

            {reportMode === "live" ? (
              <button
                className="btn btn-primary"
                disabled={!params || rows.length === 0}
              >
                <CsvDownloader
                  filename="Ticket Lifecycle"
                  datas={rows}
                  columns={columns.map((col) => ({
                    id: col.field,
                    displayName: col.headerName,
                  }))}
                  separator=","
                  wrapColumnChar={`"`}
                >
                  Download
                </CsvDownloader>
              </button>
            ) : (
              <button
                className="btn btn-primary"
                onClick={handleDownload}
                disabled={!params || downloadLoading}
              >
                {downloadLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Download
                  </>
                ) : (
                  "Download"
                )}
              </button>
            )}

            <button className="btn btn-link" onClick={resetFilters}>
              <i className="bi bi-funnel fs-2 text-dark"></i>
            </button>
          </div>
        </div>

        {/* DataGrid */}
        <CardContent style={{ width: "100%", minHeight: "400px" }}>
          {showSpinner ? (
            <LoadingSpinner />
          ) : (
            <DataGrid
              rows={rows}
              columns={columns}
              pagination
              {...(reportMode === "historical"
                ? {
                    paginationMode: "server" as const,
                    rowCount: historicalMeta?.count ?? 0,
                  }
                : {})}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationChange}
              loading={gridLoading}
              disableColumnMenu
              pageSizeOptions={[5, 10, 15, 20, 50]}
              sx={{
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "lightgray",
                  "&:last-child": {
                    justifyContent: "flex-end",
                  },
                  whiteSpace: "nowrap", // Prevent wrapping
                  overflow: "hidden", // Hide overflowing text
                  textOverflow: "ellipsis", // Add ellipsis for overflowing text
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "bold",
                  whiteSpace: "nowrap", // Prevent wrapping
                  overflow: "hidden", // Hide overflowing text
                  textOverflow: "ellipsis", // Add ellipsis for overflowing text
                },
                // Target the pagination select and actions by stable component classes
                "& .MuiTablePagination-select, .MuiTablePagination-actions": {
                  marginBottom: "10px",
                },
                // Target the DataGrid overlay wrapper with its stable class
                "& .MuiDataGrid-overlayWrapper": {
                  height: "50px",
                },
              }}
            />
          )}
        </CardContent>
      </div>
    </div>
  );
};

export default TicketLifeCycleReport;
